import React, { useEffect, useState } from "react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { Plus, Edit3, Trash2, X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "actualite", label: "Actualité" },
  { value: "promotion", label: "Promotion" },
  { value: "avantage", label: "Avantage" },
];

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  category: "actualite",
  tag: "",
  image_url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80",
  read_minutes: 4,
  promo_ends_at: "",
};

export default function AdminArticles() {
  const { authFetch } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | article object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    authFetch("get", "/admin/articles")
      .then((res) => setItems(res.data.items))
      .catch(() => toast.error("Erreur de chargement des articles."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authFetch]);

  const openNew = () => {
    setForm({ ...EMPTY });
    setEditing("new");
  };

  const openEdit = (article) => {
    setForm({
      slug: article.slug,
      title: article.title,
      excerpt: article.excerpt,
      body: article.body,
      category: article.category,
      tag: article.tag,
      image_url: article.image_url,
      read_minutes: article.read_minutes || 4,
      promo_ends_at: article.promo_ends_at || "",
    });
    setEditing(article);
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    if (!payload.promo_ends_at) delete payload.promo_ends_at;
    payload.read_minutes = parseInt(payload.read_minutes, 10) || 4;
    try {
      if (editing === "new") {
        await authFetch("post", "/admin/articles", payload);
        toast.success("Article créé.");
      } else {
        await authFetch("put", `/admin/articles/${editing.slug}`, payload);
        toast.success("Article mis à jour.");
      }
      close();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur de sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Supprimer définitivement « ${article.title} » ?`)) return;
    try {
      await authFetch("delete", `/admin/articles/${article.slug}`);
      toast.success("Article supprimé.");
      load();
    } catch {
      toast.error("Erreur de suppression.");
    }
  };

  return (
    <div data-testid="admin-articles-root">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#b08d57] mb-3">
            Journal du cabinet
          </div>
          <h1 className="font-serif text-4xl md:text-5xl">Articles</h1>
        </div>
        <button
          onClick={openNew}
          data-testid="admin-article-new"
          className="inline-flex items-center gap-2 bg-[#b08d57] text-[#0a192f] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#f0b74e] transition-colors"
        >
          <Plus size={16} strokeWidth={2} />
          Nouvel article
        </button>
      </div>

      {loading ? (
        <div className="text-[#efeade]/50 flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Chargement…
        </div>
      ) : (
        <div className="border border-[#efeade]/10 divide-y divide-[#efeade]/10">
          {items.map((a) => (
            <div
              key={a.slug}
              data-testid={`admin-article-row-${a.slug}`}
              className="flex flex-col md:flex-row md:items-center gap-4 p-5 hover:bg-white/5 transition-colors"
            >
              <span
                className={`text-[10px] tracking-[0.24em] uppercase font-semibold px-3 py-1 self-start ${
                  a.category === "promotion"
                    ? "bg-[#c05c3d] text-[#efeade]"
                    : a.category === "avantage"
                    ? "bg-[#b08d57] text-[#0a192f]"
                    : "bg-[#efeade]/10 text-[#efeade]"
                }`}
              >
                {a.category}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#efeade] truncate">{a.title}</div>
                <div className="text-xs text-[#efeade]/50 mt-1">
                  {a.slug} · {a.tag}
                </div>
              </div>
              <div className="flex items-center gap-2 self-end md:self-auto">
                <button
                  onClick={() => openEdit(a)}
                  data-testid={`admin-article-edit-${a.slug}`}
                  className="p-2 text-[#efeade]/60 hover:text-[#f0b74e] transition-colors"
                  aria-label="Modifier"
                >
                  <Edit3 size={16} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => remove(a)}
                  data-testid={`admin-article-delete-${a.slug}`}
                  className="p-2 text-[#efeade]/60 hover:text-[#c05c3d] transition-colors"
                  aria-label="Supprimer"
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start justify-center py-10 px-4 overflow-y-auto">
          <form
            onSubmit={save}
            data-testid="admin-article-form"
            className="w-full max-w-3xl bg-[#0a192f] border border-[#b08d57]/40 p-8 md:p-10 space-y-5"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">
                {editing === "new" ? "Nouvel article" : "Modifier l'article"}
              </h2>
              <button
                type="button"
                onClick={close}
                data-testid="admin-article-form-close"
                className="text-[#efeade]/60 hover:text-[#efeade]"
              >
                <X size={20} />
              </button>
            </div>

            <Field label="Slug (URL)" required>
              <input
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                required
                pattern="^[a-z0-9\-]+$"
                data-testid="admin-article-slug"
                disabled={editing !== "new"}
                className={inputCls}
                placeholder="par-example-nouvelle-loi-per"
              />
            </Field>

            <Field label="Titre" required>
              <input
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                required
                data-testid="admin-article-title"
                className={inputCls}
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label="Catégorie">
                <select
                  value={form.category}
                  onChange={(e) => update("category", e.target.value)}
                  data-testid="admin-article-category"
                  className={inputCls}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value} className="bg-[#0a192f]">
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tag">
                <input
                  value={form.tag}
                  onChange={(e) => update("tag", e.target.value)}
                  required
                  data-testid="admin-article-tag"
                  className={inputCls}
                  placeholder="ex. Retraite · PER"
                />
              </Field>
              <Field label="Lecture (min)">
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={form.read_minutes}
                  onChange={(e) => update("read_minutes", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="URL de l'image">
              <input
                value={form.image_url}
                onChange={(e) => update("image_url", e.target.value)}
                required
                data-testid="admin-article-image"
                className={inputCls}
                placeholder="https://…"
              />
            </Field>

            {form.category === "promotion" && (
              <Field label="Fin de l'offre (optionnel)">
                <input
                  type="datetime-local"
                  value={form.promo_ends_at ? form.promo_ends_at.slice(0, 16) : ""}
                  onChange={(e) =>
                    update(
                      "promo_ends_at",
                      e.target.value ? new Date(e.target.value).toISOString() : ""
                    )
                  }
                  className={inputCls}
                />
              </Field>
            )}

            <Field label="Chapô (résumé, 2-3 phrases)" required>
              <textarea
                value={form.excerpt}
                onChange={(e) => update("excerpt", e.target.value)}
                required
                data-testid="admin-article-excerpt"
                rows={3}
                className={inputCls + " resize-none"}
              />
            </Field>

            <Field label="Corps de l'article (paragraphes séparés par une ligne vide)" required>
              <textarea
                value={form.body}
                onChange={(e) => update("body", e.target.value)}
                required
                data-testid="admin-article-body"
                rows={10}
                className={inputCls + " resize-y font-serif text-[15px] leading-relaxed"}
              />
            </Field>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={close}
                className="px-5 py-3 text-xs tracking-[0.22em] uppercase text-[#efeade]/70 hover:text-[#efeade] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-article-save"
                className="inline-flex items-center gap-2 bg-[#b08d57] text-[#0a192f] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#f0b74e] transition-colors disabled:opacity-70"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={14} /> Enregistrement…
                  </>
                ) : (
                  <>
                    <Save size={14} /> Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-[#efeade]/25 focus:border-[#b08d57] focus:outline-none px-4 py-3 text-sm text-[#efeade] placeholder:text-[#efeade]/30 transition-colors disabled:opacity-50";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57] block mb-2">
        {label}
        {required && <span className="text-[#c05c3d] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
